/**
 * Agent Config step model — mirrors `AgentConfig` / `ToolSource` in
 * optune-utils (`usecase_info_collection.py`) so the form state can be sent
 * to `update_llm_usecase` as `agent_config` unchanged and hydrated back from
 * `usecase.agent_config` on resume.
 */

export const TOOL_SOURCE_TYPES = Object.freeze({
    manual: 'manual',
    mcp: 'mcp',
    swagger: 'swagger',
});

/** Order of the large choice cards shown before any source exists (Figma 3926-8787). */
export const TOOL_SOURCE_TYPE_ORDER = [
    TOOL_SOURCE_TYPES.mcp,
    TOOL_SOURCE_TYPES.swagger,
    TOOL_SOURCE_TYPES.manual,
];

/** The compact "Add Another One" cards use the same order (Figma 4012:16639). */
export const ADD_ANOTHER_ORDER = TOOL_SOURCE_TYPE_ORDER;

/** Choice-card label + Spark icon per source type (Figma 3926-8787). */
export const TOOL_SOURCE_TYPE_META = Object.freeze({
    [TOOL_SOURCE_TYPES.manual]: {
        label: 'Manual',
        cardTitle: 'Manual Tool Discovery',
        iconSlug: 'code',
    },
    [TOOL_SOURCE_TYPES.mcp]: { label: 'MCP', cardTitle: 'MCP', iconSlug: 'mcp' },
    [TOOL_SOURCE_TYPES.swagger]: { label: 'Swagger', cardTitle: 'Swagger', iconSlug: 'swagger' },
});

let sourceCounter = 0;
export const newToolSourceId = () => {
    sourceCounter += 1;
    const random = Math.random().toString(36).slice(2, 8);
    return `src-${Date.now().toString(36)}-${sourceCounter}-${random}`;
};

/** A fresh, empty tool source card of the given type. */
export const createToolSource = (type) => ({
    id: newToolSourceId(),
    type,
    tools_json: '',
    discovery_url: '',
    execution_url: '',
    use_same_mcp_for_execution: type === TOOL_SOURCE_TYPES.mcp,
    tools: [],
    selected_tools: [],
});

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * `{name, description}` plus `input_schema` (JSON schema of the parameters)
 * only when the source provided one — MCP discovery and pasted definitions
 * do, Swagger operations do not.
 */
const normalizeTool = (tool) => {
    if (!tool || typeof tool !== 'object') return null;
    const name = typeof tool.name === 'string' ? tool.name.trim() : '';
    if (!name) return null;
    const description =
        typeof tool.description === 'string' && tool.description.trim()
            ? tool.description.trim()
            : null;
    return isPlainObject(tool.input_schema)
        ? { name, description, input_schema: tool.input_schema }
        : { name, description };
};

/** Dedupes tools by name, dropping entries without a name. */
export const normalizeTools = (tools) => {
    const seen = new Set();
    const result = [];
    (Array.isArray(tools) ? tools : []).forEach((raw) => {
        const tool = normalizeTool(raw);
        if (!tool || seen.has(tool.name)) return;
        seen.add(tool.name);
        result.push(tool);
    });
    return result;
};

/** Backend `ToolSource` dict → form source (fills defaults, drops unknown keys). */
export const normalizeToolSource = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const type = TOOL_SOURCE_TYPE_ORDER.includes(raw.type) ? raw.type : null;
    if (!type) return null;
    const tools = normalizeTools(raw.tools);
    const toolNames = new Set(tools.map((t) => t.name));
    const selected = Array.isArray(raw.selected_tools)
        ? raw.selected_tools.filter((name) => toolNames.has(name))
        : [];
    return {
        id: typeof raw.id === 'string' && raw.id ? raw.id : newToolSourceId(),
        type,
        tools_json: typeof raw.tools_json === 'string' ? raw.tools_json : '',
        discovery_url: typeof raw.discovery_url === 'string' ? raw.discovery_url : '',
        execution_url: typeof raw.execution_url === 'string' ? raw.execution_url : '',
        use_same_mcp_for_execution:
            typeof raw.use_same_mcp_for_execution === 'boolean'
                ? raw.use_same_mcp_for_execution
                : type === TOOL_SOURCE_TYPES.mcp,
        tools,
        // Stored empty ⇒ every discovered tool is selected (backend
        // `discovered_tool_names` fallback) — surface that explicitly.
        selected_tools: selected.length ? selected : tools.map((t) => t.name),
    };
};

/** `usecase.agent_config` → list of form sources (empty when unset). */
export const toolSourcesFromAgentConfig = (agentConfig) => {
    const rawSources = Array.isArray(agentConfig?.tool_sources) ? agentConfig.tool_sources : [];
    return rawSources.map(normalizeToolSource).filter(Boolean);
};

/** A source the user has not touched at all — dropped on save. */
export const isBlankToolSource = (source) =>
    !source.tools.length &&
    !(source.tools_json || '').trim() &&
    !(source.discovery_url || '').trim() &&
    !(source.execution_url || '').trim();

export const sourceHasTools = (source) => Array.isArray(source?.tools) && source.tools.length > 0;

/** True when at least one source discovered at least one tool. */
export const agentConfigHasTools = (agentConfig) =>
    toolSourcesFromAgentConfig(agentConfig).some(sourceHasTools);

export const toolSourcesHaveTools = (sources) => (sources || []).some(sourceHasTools);

/** True when the source keeps at least one of its discovered tools checked. */
export const sourceHasSelectedTools = (source) => {
    if (!sourceHasTools(source) || !Array.isArray(source.selected_tools)) return false;
    const names = new Set(source.tools.map((t) => t.name));
    return source.selected_tools.some((name) => names.has(name));
};

/** True when at least one tool is checked across all sources — Next requires it. */
export const toolSourcesHaveSelectedTools = (sources) => (sources || []).some(sourceHasSelectedTools);

/** Effective execution URL of a source (MCP may reuse the discovery link). */
export const effectiveExecutionUrl = (source) => {
    if (source.type === TOOL_SOURCE_TYPES.mcp && source.use_same_mcp_for_execution) {
        return (source.discovery_url || '').trim();
    }
    return (source.execution_url || '').trim();
};

const looksLikeUrl = (value) => /^https?:\/\/\S+$/i.test((value || '').trim());

/** Single message for the "Tool Execution (MCP/Rest API)" URL (Figma 4012:16639). */
export const EXECUTION_URL_ERROR = 'Enter the tool execution URL (https://…)';

/**
 * Per-field errors of one source, keyed `tools` / `discovery_url` /
 * `execution_url`. Blank sources are not validated (they are dropped).
 */
export const toolSourceErrors = (source) => {
    const errors = {};
    if (isBlankToolSource(source)) return errors;
    if (!sourceHasTools(source)) {
        errors.tools =
            source.type === TOOL_SOURCE_TYPES.manual
                ? 'Paste at least one tool definition'
                : 'Discover at least one tool from the link';
    }
    if (source.type !== TOOL_SOURCE_TYPES.manual) {
        if (!looksLikeUrl(source.discovery_url)) {
            errors.discovery_url =
                source.type === TOOL_SOURCE_TYPES.mcp
                    ? 'Enter a valid MCP link (https://…)'
                    : 'Enter a valid Swagger / OpenAPI link (https://…)';
        }
    }
    const reusesDiscoveryLink =
        source.type === TOOL_SOURCE_TYPES.mcp && source.use_same_mcp_for_execution;
    if (!reusesDiscoveryLink && !looksLikeUrl(source.execution_url)) {
        errors.execution_url = EXECUTION_URL_ERROR;
    }
    return errors;
};

/** Sources → `agent_config` payload for `update_llm_usecase`. */
export const agentConfigPayload = (sources) => ({
    tool_sources: (sources || [])
        .filter((source) => !isBlankToolSource(source))
        .map((source) => ({
            id: source.id,
            type: source.type,
            tools_json: source.type === TOOL_SOURCE_TYPES.manual ? source.tools_json || null : null,
            discovery_url:
                source.type === TOOL_SOURCE_TYPES.manual
                    ? null
                    : (source.discovery_url || '').trim() || null,
            execution_url: effectiveExecutionUrl(source) || null,
            use_same_mcp_for_execution:
                source.type === TOOL_SOURCE_TYPES.mcp ? Boolean(source.use_same_mcp_for_execution) : false,
            tools: source.tools.map((t) => ({
                name: t.name,
                description: t.description ?? null,
                input_schema: t.input_schema ?? null,
            })),
            selected_tools: source.selected_tools,
        })),
});
